import { useState, useRef } from 'react'
import { useDebouncedValue } from '@/lib/hooks'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { motion } from 'framer-motion'
import { Plus, Search, Users, Mail, Phone, MapPin, Upload, Download, Loader2 } from 'lucide-react'
import { candidatesApi, api } from '@/lib/api'
import { PageLayout } from '@/components/layout/PageLayout'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { toast } from '@/components/ui/toaster'
import { getInitials, formatRelativeTime } from '@/lib/utils'

// CSV Import Dialog
function CsvImportDialog({ onSuccess }: { onSuccess: () => void }) {
  const [open, setOpen] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [result, setResult] = useState<{ imported: number; skipped: number; errors: string[] } | null>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  const handleImport = async (file: File) => {
    setIsUploading(true)
    setResult(null)
    try {
      const text = await file.text()
      const res = await api.post<any>('/candidates/import', { csv: text })
      setResult(res)
      if (res.imported > 0) {
        onSuccess()
        toast({
          title: 'Import complete',
          description: `${res.imported} candidates imported, ${res.skipped} skipped`,
          variant: 'success',
        })
      }
    } catch (error: any) {
      toast({ title: 'Import failed', description: error.message, variant: 'destructive' })
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setResult(null) }}>
      <DialogTrigger asChild>
        <Button variant="outline">
          <Upload className="h-4 w-4 mr-2" />
          Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Import Candidates from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV file with columns: email, firstName, lastName, phone, source, currentTitle, currentCompany
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="border-2 border-dashed rounded-lg p-8 text-center">
            <input
              ref={fileRef}
              type="file"
              accept=".csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0]
                if (file) handleImport(file)
              }}
            />
            {isUploading ? (
              <div className="flex flex-col items-center gap-2">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Importing candidates...</p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-2">
                <Upload className="h-8 w-8 text-muted-foreground" />
                <Button variant="outline" onClick={() => fileRef.current?.click()}>
                  Choose CSV File
                </Button>
                <p className="text-xs text-muted-foreground">
                  Headers must be in the first row. Email is required.
                </p>
              </div>
            )}
          </div>
          {result && (
            <div className="rounded-lg border p-4 space-y-2">
              <p className="text-sm font-medium">Import Results</p>
              <p className="text-sm text-muted-foreground">
                {result.imported} imported, {result.skipped} skipped
              </p>
              {result.errors && result.errors.length > 0 && (
                <div className="text-xs text-destructive max-h-24 overflow-y-auto">
                  {result.errors.map((err: string, i: number) => (
                    <p key={i}>{err}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function CandidatesPage() {
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search, 300)
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['candidates', { search: debouncedSearch }],
    queryFn: () => candidatesApi.list({ ...(debouncedSearch && { search: debouncedSearch }) }),
  })

  const candidates = (data as any)?.items || []

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search candidates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9 h-8"
        />
      </div>
      <Button
        variant="outline"
        size="sm"
        onClick={async () => {
          try {
            const token = useAuthStore.getState().accessToken
            const apiBase = import.meta.env.VITE_API_URL
              ? `${import.meta.env.VITE_API_URL}/api/v1`
              : '/api/v1'
            const response = await fetch(`${apiBase}/candidates/export`, {
              headers: { Authorization: `Bearer ${token}` },
            })
            if (!response.ok) throw new Error('Export failed')
            const blob = await response.blob()
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = 'candidates.csv'
            a.click()
            URL.revokeObjectURL(url)
          } catch {
            toast({ title: 'Export failed', variant: 'destructive' })
          }
        }}
      >
        <Download className="h-4 w-4 mr-1" />
        Export
      </Button>
      <CsvImportDialog onSuccess={() => queryClient.invalidateQueries({ queryKey: ['candidates'] })} />
      <Button size="sm" asChild>
        <Link to="/candidates/new">
          <Plus className="h-4 w-4 mr-1" />
          Add
        </Link>
      </Button>
    </div>
  )

  return (
    <PageLayout fullHeight toolbar={toolbar}>
      {/* Candidates List */}
      <div className="h-full lg:overflow-y-auto">
      {isLoading ? (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-40 skeleton rounded-lg" />
          ))}
        </div>
      ) : candidates.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-1">No candidates found</h3>
            <p className="text-muted-foreground mb-4">
              {search
                ? 'Try adjusting your search'
                : 'Add candidates to your talent pool'}
            </p>
            {!search && (
              <Button asChild>
                <Link to="/candidates/new">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Candidate
                </Link>
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {candidates.map((candidate: any, index: number) => (
            <motion.div
              key={candidate.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15, delay: index * 0.03 }}
            >
              <Link to="/candidates/$candidateId" params={{ candidateId: candidate.id }}>
                <Card interactive className="h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback>
                          {getInitials(`${candidate.firstName} ${candidate.lastName}`)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">
                          {candidate.firstName} {candidate.lastName}
                        </h3>
                        {candidate.currentTitle && (
                          <p className="text-sm text-muted-foreground truncate">
                            {candidate.currentTitle}
                            {candidate.currentCompany && ` at ${candidate.currentCompany}`}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-4 space-y-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-3.5 w-3.5" />
                        <span className="truncate">{candidate.email}</span>
                      </div>
                      {candidate.phone && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Phone className="h-3.5 w-3.5" />
                          <span>{candidate.phone}</span>
                        </div>
                      )}
                      {candidate.location && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5" />
                          <span>{candidate.location}</span>
                        </div>
                      )}
                    </div>

                    {candidate.skills && candidate.skills.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-1">
                        {candidate.skills.slice(0, 3).map((skill: string) => (
                          <Badge key={skill} variant="secondary" className="text-xs">
                            {skill}
                          </Badge>
                        ))}
                        {candidate.skills.length > 3 && (
                          <Badge variant="secondary" className="text-xs">
                            +{candidate.skills.length - 3}
                          </Badge>
                        )}
                      </div>
                    )}

                    <div className="mt-4 pt-4 border-t flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        Added {formatRelativeTime(candidate.createdAt)}
                      </span>
                      {candidate.source && (
                        <Badge variant="outline" className="text-xs">
                          {candidate.source}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      )}
      </div>
    </PageLayout>
  )
}
